import {
  AnyAction,
  AsyncThunk,
  ActionReducerMapBuilder,
  createSlice,
  SerializedError,
  SliceCaseReducers,
  ValidateSliceCaseReducers,
} from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { IEachTotalSales } from '../model/each-total-sales.model';
import { ICommodity, ICustomerCommoditiesSold, ITopCommodities } from '../model/commodity.model';
import { IQuotationCalculation } from 'app/shared/model/quotation-calculation.model';

/**
 * Model for redux actions with pagination
 */
export type IQueryParams = {
  query?: string;
  page?: number;
  size?: number | string;
  sort?: string;
  createdBy?: string;
  search?: string;
  date?: string;
  status?: string[];
  tab?: string;
  quotationTab?: string;
  curDate?: string;
  poTab?: string;

  userId?: number;
  bankManagerId?: number;
  areaManagerId?: number;
  customerLogin?: string;
  bankId?: number;
  branchId?: number;
  commodityId?: number;
  customerId?: number;
  searchQuo?: string;
  fromDate?: string | Date;
  toDate?: string | Date;

  bulkTransactionId?: number;
  quoMinDate?: string;
  commodityType?: string;

  userTimezone?: string;

  amountTransferred?: string;
};

export type IReportQueryParam = {
  bankId?: string | string[];
  branchId?: string | string[];
  bankUserId?: string | string[];
  commodityId?: string | string[];
  fromDate?: string;
  toDate?: string;
  status?: string[];
  page?: number;
  size?: number;
  selectOption?: string;
  commodityTypeId?: string | string[];
};

export type ISummaryQueryParam = {
  id?: string | number;
  userRole?: string;
  commodityType?: string;
};

/**
 * Useful types for working with actions
 */
type GenericAsyncThunk = AsyncThunk<unknown, unknown, any>;
export type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
export type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;
export type FulfilledAction = ReturnType<GenericAsyncThunk['fulfilled']>;

/**
 * Check if the async action type is rejected
 */
export function isRejectedAction(action: AnyAction) {
  return action.type.endsWith('/rejected');
}

/**
 * Check if the async action type is pending
 */
export function isPendingAction(action: AnyAction) {
  return action.type.endsWith('/pending');
}

/**
 * Check if the async action type is completed
 */
export function isFulfilledAction(action: AnyAction) {
  return action.type.endsWith('/fulfilled');
}

const commonErrorProperties: Array<keyof SerializedError> = ['name', 'message', 'stack', 'code'];

/**
 * serialize function used for async action errors,
 * since the default function from Redux Toolkit strips useful info from axios errors
 */
export const serializeAxiosError = (value: any): AxiosError | SerializedError => {
  if (typeof value === 'object' && value !== null) {
    if (value.isAxiosError) {
      return value;
    } else {
      const simpleError: SerializedError = {};
      for (const property of commonErrorProperties) {
        if (typeof value[property] === 'string') {
          simpleError[property] = value[property];
        }
      }

      return simpleError;
    }
  }
  return { message: String(value) };
};

export interface EntityState<T> {
  loading: boolean;
  errorMessage: string | null;
  entities: T[];
  entity: T;
  links?: any;
  updating: boolean;
  totalItems?: number;
  updateSuccess: boolean;
  valueSold?: T[];
  valueSoldLoadingSuccess?: boolean;
  topCommodities?: ITopCommodities[];
  customerSoldCommodities?: ICustomerCommoditiesSold[];
  totalCustomer?: T[];
  performance?: T[];
  branchForStaffAndManager?: T;
  branchPerformance?: T[];
  quotationCreatedCount?: number;
  quotationSoldCount?: number;
  quotationPendingTransferCount?: number;
  quotationPurchaseOrderCount?: number;
  quotationKeepCount?: number;
  commoditiesPriceList?: T[];
  quotationPOApprovalToday?: number;
  quotationcancellationApprovalToday?: number;
  quotationKeepApprovalToday?: number;
  eachTotalSales?: IEachTotalSales[];
  totalSoldToday?: number;
  reportPrint?: T[];
  updateCancellationDecisionFromBursa?: number;
  keepOrSellOrdersTodayForCustomer?: number;
  currentMonthEntities?: T[];
  staleThreshold?: number;
  quotationSoldCountLocal?: number;
  quotationSoldCountInternational?: number;
  quotationCreatedCountLocal?: number;
  quotationCreatedCountInternational?: number;
  quotationPurchaseOrderCountLocal?: number;
  quotationPurchaseOrderCountInternational?: number;
  quotationPurchaseAndTransferCountLocal?: number;
  quotationPurchaseAndTransferCountInternational?: number;
  quotationKeepCountLocal?: number;
  quotationKeepCountInternational?: number;
  totalSoldTodayLocal?: number;
  totalSoldTodayInternational?: number;
  totalSoldTodayOtherCurrencies?: Array<{ currency: string; amount: number }>;
  resendSmsLoading?: boolean;
  resendSmsSuccess?: boolean;
  resendSmsError?: string | null;
  verifyCommodityName?: ICommodity;
  valueTransferred?: T[];
  valueTransferredLoadingSuccess?: boolean;
  valueBought?: T[];
  valueBoughtLoadingSuccess?: boolean;
  valueKept?: T[];
  valueKeptLoadingSuccess?: boolean;
  valueCancelled?: T[];
  valueCancelledLoadingSuccess?: boolean;
  valueQuoted?: T[];
  valueQuotedLoadingSuccess?: boolean;

  calculationLoading?: boolean;
  calculationResult?: IQuotationCalculation;
  calculationError?: string | null;

  bookingConfirmationTotalItems?: number;
}

/**
 * A wrapper on top of createSlice from Redux Toolkit to extract
 * common reducers and matchers used by entities
 */
export const createEntitySlice = <T, Reducers extends SliceCaseReducers<EntityState<T>>>({
  name = '',
  initialState,
  reducers,
  extraReducers,
  skipRejectionHandling,
}: {
  name: string;
  initialState: EntityState<T>;
  reducers?: ValidateSliceCaseReducers<EntityState<T>, Reducers>;
  extraReducers?: (builder: ActionReducerMapBuilder<EntityState<T>>) => void;
  skipRejectionHandling?: boolean;
}) => {
  return createSlice({
    name,
    initialState,
    reducers: {
      /**
       * Reset the entity state to initial state
       */
      reset() {
        return initialState;
      },
      ...reducers,
    },
    extraReducers(builder) {
      extraReducers(builder);
      /*
       * Common rejection logic is handled here.
       * If you want to add your own rejcetion logic, pass `skipRejectionHandling: true`
       * while calling `createEntitySlice`
       * */
      if (!skipRejectionHandling) {
        builder.addMatcher(isRejectedAction, (state, action) => {
          state.loading = false;
          state.updating = false;
          state.updateSuccess = false;
          state.errorMessage = action.error.message;
        });
      }
    },
  });
};
